import type { UnknownAction } from '@reduxjs/toolkit';
import type { RecordOf } from 'immutable';

import { crashReporterInterface } from '../../utils/core';
import { hasAsyncDispatch } from '../../utils/redux-store/asyncDispatchMiddleware';
import { accountsActions } from '../accounts';
import { featureFlagsSelectors } from '../feature-flags';
import { institutionLoginsActions } from '../institution-logins';
import * as actions from './accountDiscoveryActions';
import {
  type InstitutionLoginUpserterDataProps,
  type InstitutionLoginUpserterStore,
  mkInstitutionLoginUpserterData,
  mkInstitutionLoginUpserterStore,
} from './accountDiscoveryTypes';
import { getQcsAggregationAction } from './aggErrors/errors';

const getDataForScope = (state: InstitutionLoginUpserterStore, scope: string | undefined) => {
  assert(scope, 'Cannot access InstitutionLoginUpserterisAuthenticationDoneStore without a scope');
  return state.upserterData.get(scope!);
};

const setDataForScope = (
  state: InstitutionLoginUpserterStore,
  scope: string | undefined,
  data: RecordOf<InstitutionLoginUpserterDataProps>,
) => {
  assert(scope, 'Cannot access InstitutionLoginUpserterStore without a scope');
  return state.set('upserterData', state.upserterData.set(scope!, data));
};

const mergeDataForScope = (
  state: InstitutionLoginUpserterStore,
  scope: string | undefined,
  data: Partial<InstitutionLoginUpserterDataProps>,
) => {
  assert(scope, 'Cannot access InstitutionLoginUpserterStore without a scope');
  const scopedData = assert(getDataForScope(state, scope), 'can not get data for scope (mergeDataForScope)') as
    | RecordOf<InstitutionLoginUpserterDataProps>
    | null
    | undefined;
  return setDataForScope(state, scope, (scopedData ?? mkInstitutionLoginUpserterData()).merge(data));
};

export const accountDiscoveryReducer = (
  state: InstitutionLoginUpserterStore = mkInstitutionLoginUpserterStore(),
  action: UnknownAction,
): InstitutionLoginUpserterStore => {
  switch (true) {
    case actions.accountDiscoverySetup.match(action): {
      const scope = action.meta?.scope;
      crashReporterInterface.addBreadcrumb?.('Account Discovery Setup', { scope }, 'state');
      return setDataForScope(state, scope, mkInstitutionLoginUpserterData(action.payload));
    }

    case actions.accountDiscoveryCleanup.match(action): {
      const scope = action.meta?.scope;
      crashReporterInterface.addBreadcrumb?.('Account Discovery Cleanup', { scope }, 'state');
      return state.set('upserterData', state.upserterData.remove(scope!));
    }

    case actions.institutionSelected.match(action): {
      const { channel, institution } = action.payload;
      const scope = action.meta?.scope;
      crashReporterInterface.addBreadcrumb?.('Account Discovery Institution Selected', { scope }, 'state');
      const data = getDataForScope(state, scope);

      assert(institution, 'Missing Institution in institutionSelected reducer');

      const institutionLogin = data?.institutionLogin
        ? data.institutionLogin.set('institutionId', institution?.id || data.institutionLogin.institutionId)
        : null;
      return mergeDataForScope(state, scope, { channel, institution, institutionLogin });
    }

    case actions.accountDiscoveryCredentialsFormSubmit.match(action):
      return mergeDataForScope(state, action.meta?.scope, {
        error: false,
        isSubmitting: true,
      });

    case actions.accountDiscoveryCredentialsFormSubmitResponse.match(action): {
      const { payload } = action;
      const scope = action.meta?.scope;
      if (payload instanceof Error || !('institutionLogin' in payload)) {
        return mergeDataForScope(state, scope, { isSubmitting: false });
      }
      return mergeDataForScope(state, scope, {
        institutionLogin: payload.institutionLogin,

        isPolling: true,
        isSubmitting: false,
      });
    }

    case actions.accountDiscoveryMfaRequired.match(action):
      return mergeDataForScope(state, action.meta?.scope, {
        mfaChallenge: action.payload.mfaChallenge,

        isPolling: false,
        isSubmitting: false,
      });

    case actions.accountDiscoveryMfaFormSubmit.match(action):
      return mergeDataForScope(state, action.meta?.scope, {
        error: null,

        isSubmitting: true,
      });

    case actions.accountDiscoveryMfaFormSubmitResponse.match(action): {
      const scope = action.meta?.scope;
      if (!('error' in action && action.error)) {
        return mergeDataForScope(state, scope, {
          mfaChallenge: null,

          isPolling: true,
          isSubmitting: false,
        });
      }
      return mergeDataForScope(state, scope, {
        mfaChallenge: null,

        isSubmitting: false,
      });
    }

    case actions.getPartnerAuthUris.match(action):
      return mergeDataForScope(state, action.meta?.scope, {
        error: false,
      });

    case actions.getPartnerAuthUrisSuccess.match(action):
      return mergeDataForScope(state, action.meta?.scope, {
        partnerAuthUris: action.payload.partnerAuthUris,
      });

    case actions.getPartnerAuthUrisFailure.match(action): {
      const { error } = action.payload;
      const status = error?.response?.status;
      const code = error?.response?.data?.errors?.[0]?.code;
      // const description = payload?.response?.data?.errors?.[0]?.description;
      return mergeDataForScope(state, action.meta?.scope, {
        error: {
          type: 'ERROR',
          id: status,
          title: 'This Bank is not available right now',
          text: 'This Bank is not available right now.',
          status,
          statusCode: code,
          code: null,
          // actionLabel: 'Try Again',
          // action: refreshAccounts({ institutionLoginId: institutionLogin.id }),
          // showConfirmationDialog: true,
          // institutionLogin,
        },
      });
    }

    case actions.accountDiscoverySuccessWithAccounts.match(action): {
      const { institutionLogin, accountDiscoveryData } = action.payload;
      const scope = action.meta?.scope;
      if (accountDiscoveryData.credentialsBlob && hasAsyncDispatch(action)) {
        action.asyncDispatch(
          institutionLoginsActions.institutionLoginSetCredentialsBlob({
            institutionLoginId: accountDiscoveryData.institutionLoginId as string,
            credentialsBlob: accountDiscoveryData.credentialsBlob,
          }),
        );
      }
      return mergeDataForScope(state, scope, {
        institutionLogin,
        accountDiscoveryData,

        isAuthenticationDone: true,
        isPolling: false,
        isSubmitting: false,
      });
    }

    case actions.accountDiscoverySuccess.match(action):
      return mergeDataForScope(state, action.meta?.scope, {
        institutionLogin: action.payload.institutionLogin,
        isAuthenticationDone: true,
        isPolling: false,
        isSubmitting: false,
      });

    case actions.accountDiscoveryFailure.match(action): {
      const { adStatus, institutionLogin } = action.payload;
      let error = null;
      if (adStatus.status === 'AGGREGATOR_IN_ERROR') {
        const aggregator = adStatus.aggregators.get(0);
        if (aggregator) {
          const { aggStatus, cpAggStatusCode, userInstructions } = aggregator;
          error = getQcsAggregationAction(
            institutionLogin,
            aggStatus,
            cpAggStatusCode,
            featureFlagsSelectors.dangerouslyUseFeatureFlags(),
            userInstructions ? new Error(userInstructions) : undefined,
          );
        }
      }
      return mergeDataForScope(state, action.meta?.scope, {
        institutionLogin,
        error,

        isPolling: false,
        isSubmitting: false,
      });
    }

    case actions.accountDiscoveryError.match(action): {
      const { institutionLogin, error } = action.payload;
      const aggStatus = error?.aggStatus || error?.response?.status;
      const aggStatusCode = error?.aggStatusCode || error?.response?.data?.errors?.[0]?.code;

      const aggAction = getQcsAggregationAction(
        institutionLogin,
        aggStatus,
        aggStatusCode,
        featureFlagsSelectors.dangerouslyUseFeatureFlags(),
        error,
      );
      return mergeDataForScope(state, action.meta?.scope, {
        institutionLogin,
        error: aggAction,

        isPolling: false,
        isSubmitting: false,
      });
    }

    case actions.accountDiscoveryStopped.match(action):
      return mergeDataForScope(state, action.meta?.scope, {
        isPolling: false,
        isSubmitting: false,
      });

    case actions.accountDiscoveryWidgetUpdateSuccess.match(action): {
      const { institutionLogin, accountDiscoveryData } = action.payload;
      return mergeDataForScope(state, action.meta?.scope, {
        institutionLogin,
        accountDiscoveryData,

        isAuthenticationDone: true,
      });
    }

    case actions.discoverInstitutionLoginWithAccounts.match(action):
      return mergeDataForScope(state, action.meta?.scope, {
        institutionLogin: action.payload.institutionLogin,
        isSubmitting: true,
      });

    case actions.discoverInstitutionLoginWithAccountsResponse.match(action): {
      const { payload } = action;
      const scope = action.meta?.scope;
      if (payload instanceof Error || !('institutionLogin' in payload)) {
        return mergeDataForScope(state, scope, {
          isSubmitting: false,
        });
      }
      const { institutionLogin, accountDiscoveryData, updateStrategy } = payload;
      const institutionLoginPropsToMerge = {
        ...(!institutionLogin.institutionId && { institutionId: undefined }),
        ...(updateStrategy === 'REPLACE' && { cpSetupMode: 'DISCOVER_AND_REPLACE_ACCOUNTS' }),
        ...(updateStrategy === 'UPDATE' && { cpSetupMode: 'DISCOVER_AND_ADD_ACCOUNTS' }),
      };
      const mergedInstitutionLogin = institutionLogin.merge(institutionLoginPropsToMerge);

      return mergeDataForScope(state, scope, {
        institutionLogin: mergedInstitutionLogin,
        accountDiscoveryData,
        updateStrategy,

        isAuthenticationDone: true,
        isSubmitting: false,
      });
    }

    case actions.discoverInstitutionLoginWithAccountsDiscard.match(action):
      return mergeDataForScope(state, action.meta?.scope, {
        institutionLogin: null,
        isSubmitting: false,
      });

    case institutionLoginsActions.addInstitutionLoginAndAccountsResponse.match(action): {
      const { institutionLogin } = action.payload;
      const scope = action.meta?.scope;
      // On failure the saga dispatches the caught error as the payload without an FSA error flag,
      // so this branch also runs for errors and merges an empty institutionLogin.
      if (!('error' in action && action.error)) {
        if (!scope) {
          return state;
        }
        return mergeDataForScope(state, scope, {
          institutionLogin,

          isAccountUpsertingDone: true,
          isSubmitting: false,
        });
      }
      if (!scope) {
        return state;
      }
      return mergeDataForScope(state, scope, {
        isSubmitting: false,
      });
    }

    case institutionLoginsActions.refreshAccountsCredentialsProvided.match(action): {
      const scope = action.meta?.scope;
      if (!scope) return state;
      return mergeDataForScope(state, scope, { isAuthenticationDone: true });
    }

    case institutionLoginsActions.refreshAccountsMfaFormSubmitResponse.match(action): {
      const { institutionLoginId } = action.payload;
      const scope = action.meta?.scope;
      if (!scope) return state;
      const data = getDataForScope(state, scope);
      if (data?.institutionLogin?.id && data.institutionLogin.id === institutionLoginId) {
        return mergeDataForScope(state, scope, { isAuthenticationDone: true });
      }
      return state;
    }

    case accountsActions.batchAccountsSuccess.match(action):
    case accountsActions.batchAccountsFailure.match(action): {
      const scope = action.meta?.scope;
      if (!scope) return state;
      return mergeDataForScope(state, scope, {
        isAccountUpsertingDone: true,
        isSubmitting: false,
      });
    }

    default:
      return state;
  }
};
